import React, {useCallback, useEffect} from 'react';

import {setWindowTitle} from 'BenchlingUtil';
import ChemicalCounterpartLibrary from 'chemistry/ChemicalCounterpartLibrary';
import {
  archiveSalts,
  archiveSolvents,
  createSalt,
  SALT_CREATE_FROM_CSV_URL,
  unarchiveSalts,
  unarchiveSolvents,
  updateSalt,
  updateSolvent,
} from 'chemistry/ChemistryDAO';
import {SALT_LIBRARY_QUERY} from 'chemistry/salts/SaltLibraryQuery';
import {SolventLibraryQuery} from 'chemistry/solvents/__generated/SolventLibraryQuery';
import {SOLVENT_LIBRARY_QUERY} from 'chemistry/solvents/SolventLibraryQuery';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {TABLE_KEYS} from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil from 'util/APIUtil';
import {withLoadingScreen} from 'util/Promises';

import {SaltLibraryQuery} from './salts/__generated/SaltLibraryQuery';

// Temporary until the backend consolidates salts and solvents into one type: route each row's
// mutation to the salt or solvent endpoint based on its API ID type.
function isSolvate(id: string): boolean {
  return APIUtil.apiIdToType(id) === ModelTypeConstants.SOLVENT;
}

function partitionByType(ids: Array<string>): {saltIds: Array<string>; solvateIds: Array<string>} {
  const saltIds: Array<string> = [];
  const solvateIds: Array<string> = [];
  for (const id of ids) {
    if (isSolvate(id)) {
      solvateIds.push(id);
    } else {
      saltIds.push(id);
    }
  }
  return {saltIds, solvateIds};
}

export default function SaltSolvateLibrary(): JSX.Element {
  useEffect(() => {
    setWindowTitle('Salt & Solvate');
  }, []);

  const saltResult = useBenchlingQuery<SaltLibraryQuery>(SALT_LIBRARY_QUERY);
  const solvateResult = useBenchlingQuery<SolventLibraryQuery>(SOLVENT_LIBRARY_QUERY);

  const refetchAll = useCallback(async () => {
    await Promise.all([saltResult.refetch(), solvateResult.refetch()]);
  }, [saltResult, solvateResult]);

  const salts = saltResult.type === QueryResultType.SUCCESS ? saltResult.data.salts : [];
  const solvates = solvateResult.type === QueryResultType.SUCCESS ? solvateResult.data.solvents : [];

  return (
    <ChemicalCounterpartLibrary
      counterpartName="salt or solvate"
      libraryTitle="Salt & Solvate library"
      items={[...salts, ...solvates]}
      loading={saltResult.type === QueryResultType.PENDING || solvateResult.type === QueryResultType.PENDING}
      tableKey={TABLE_KEYS.SALT_SOLVATE_LIBRARY_TABLE}
      importFromCSVUrl={SALT_CREATE_FROM_CSV_URL}
      onAddNew={async (name: string, code: string, sketcherValue: string) => {
        // New entries are always created as salts (type SALT, `slt_` prefix); users never designate
        // salt vs. solvate.
        const response = await createSalt(name, sketcherValue, code);
        await saltResult.refetch();
        return {wasUncharged: response.wasUncharged};
      }}
      onEdit={async (id: string, name: string, code: string) => {
        if (isSolvate(id)) {
          await updateSolvent(id, name, code);
        } else {
          await updateSalt(id, name, code);
        }
        await refetchAll();
      }}
      onArchive={async (ids, purpose) => {
        const {saltIds, solvateIds} = partitionByType(ids);
        await withLoadingScreen(
          Promise.all([
            saltIds.length > 0 ? archiveSalts(saltIds, purpose) : Promise.resolve(),
            solvateIds.length > 0 ? archiveSolvents(solvateIds, purpose) : Promise.resolve(),
          ])
        );
        await refetchAll();
      }}
      onUnarchive={async (ids) => {
        const {saltIds, solvateIds} = partitionByType(ids);
        await withLoadingScreen(
          Promise.all([
            saltIds.length > 0 ? unarchiveSalts(saltIds) : Promise.resolve(),
            solvateIds.length > 0 ? unarchiveSolvents(solvateIds) : Promise.resolve(),
          ])
        );
        await refetchAll();
      }}
    />
  );
}
